import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import {
    AssetSearchRequest,
    AssetSearchRequestDto,
    GradedSummary,
    toAssetSearchRequestDto,
} from "services/asset-management/AssetService";

const ASSET_MANAGEMENT_PATH = "/api/v1/asset-management/";
const GRADING_STEPS_PATH = "/api/v1/grading-steps";
const WORKFLOWS_PATH = "/api/v1/workflows/grading";
const HTTP_NOT_FOUND = 404;

// TODO BCC-7908 Uncomment for Device Diagnostics step
export enum GradingStep {
    SMART_GRADING_STARTED = "SMART_GRADING_STARTED",
    WORKFLOW_INTEGRATION_STARTED = "WORKFLOW_INTEGRATION_STARTED",
    //DEVICE_DIAGNOSED = "DEVICE_DIAGNOSED",
    VALUE_RECOVERY = "VALUE_RECOVERY",
}

export interface GradingStepProgressDto {
    group_uuid: string;
    step: GradingStep;
    workflow_uuid?: string;
    enabled: boolean;
    edited_date: string;
    rerun_count?: number;
}

interface GradingStepProgressListDto {
    grading_steps: GradingStepProgressDto[];
}

export interface GradingStepProgress {
    groupUuid: string;
    step: GradingStep;
    workflowUuid?: string;
    enabled: boolean;
    editedDate: string;
    rerunCount?: number;
}

interface SaveGradingStepDto {
    step: GradingStep;
    enabled: boolean;
    workflow_uuid?: string;
    is_rerun?: boolean;
}

interface GradedSummaryDto {
    total_graded: number;
    total_pending: number;
    total_locked: number;
}

interface AssetValuesDto {
    value: number;
    uplift_value: number;
}

export interface AssetValues {
    value: number;
    upliftValue: number;
}

// TODO BCC-7908 Uncomment for Device Diagnostics step
const GRADING_STEP_TO_STEPPER_NUMBER: Record<GradingStep, number> = {
    [GradingStep.SMART_GRADING_STARTED]: 2,
    [GradingStep.WORKFLOW_INTEGRATION_STARTED]: 3,
    //[GradingStep.DEVICE_DIAGNOSED]: 4,
    [GradingStep.VALUE_RECOVERY]: 3,
};

export const FIRST_STEPPER_NUMBER = 1;

export function toStepperNumber(step?: GradingStep): number {
    if (step === undefined) {
        return FIRST_STEPPER_NUMBER;
    }
    const stepperNumber = GRADING_STEP_TO_STEPPER_NUMBER[step];
    if (stepperNumber === undefined) {
        throw new Error("Unknown grading step: " + step);
    }
    return stepperNumber;
}

function toGradingStepProgress(dto: GradingStepProgressDto): GradingStepProgress {
    return {
        groupUuid: dto.group_uuid,
        step: dto.step,
        workflowUuid: dto.workflow_uuid,
        enabled: dto.enabled,
        editedDate: dto.edited_date,
        rerunCount: dto.rerun_count,
    };
}

export interface GradingLaunchResponse {
    assetsToGradeCount: number;
}

interface GradingLaunchResponseDto {
    assets_to_grade_count: number;
}

export interface ResolveRiskSummary {
    lockedAssetsCount: number;
    quarantinedAssetsCount: number;
    lockedAndQuarantinedAssetsCount: number;
    assetsExcludingLockedAndQuarantinedCount: number;
}

interface ResolveRiskSummaryDto {
    locked_assets_count: number;
    quarantined_assets_count: number;
    locked_and_quarantined_assets_count: number;
    assets_excluding_locked_and_quarantined_count: number;
}

function toResolveRiskSummary(dto: ResolveRiskSummaryDto): ResolveRiskSummary {
    return {
        lockedAssetsCount: dto.locked_assets_count,
        quarantinedAssetsCount: dto.quarantined_assets_count,
        lockedAndQuarantinedAssetsCount: dto.locked_and_quarantined_assets_count,
        assetsExcludingLockedAndQuarantinedCount: dto.assets_excluding_locked_and_quarantined_count,
    };
}

interface WorkflowLaunchParameterDto {
    asset_id: string;
}
interface WorkflowForGradingDto {
    name: string;
    uuid: string;
    launch_parameters: WorkflowLaunchParameterDto;
    description: string;
    workflow_uuid?: string;
}
interface WorkflowsForGradingDto {
    templates: WorkflowForGradingDto[];
    workflows: WorkflowForGradingDto[];
}

export interface WorkflowLaunchParameter {
    assetId: string;
}
function toWorkflowLaunchParameter(dto: WorkflowLaunchParameterDto): WorkflowLaunchParameter {
    return {
        assetId: dto.asset_id,
    };
}
export interface WorkflowForGrading {
    name: string;
    uuid: string;
    launchParameters: WorkflowLaunchParameter;
    description: string;
    workflowUuid?: string;
}

function toWorkflowForGrading(dto: WorkflowForGradingDto): WorkflowForGrading {
    return {
        description: dto.description,
        launchParameters: toWorkflowLaunchParameter(dto.launch_parameters),
        name: dto.name,
        uuid: dto.uuid,
        workflowUuid: dto.workflow_uuid,
    };
}

export interface WorkflowsForGrading {
    templates: WorkflowForGrading[];
    workflows: WorkflowForGrading[];
}

function toWorkflowsForGrading(dto: WorkflowsForGradingDto): WorkflowsForGrading {
    return {
        templates: dto.templates.map((each) => toWorkflowForGrading(each)),
        workflows: dto.workflows.map((each) => toWorkflowForGrading(each)),
    };
}

export interface CosmeticGradingImage {
    imageType: string;
    presignedImageUrl?: string;
    detectedDamageTypes: string[];
    debugPresignedImageUrl?: string;
    privacyPresignedImageUrl?: string;
    damagePresignedImageUrl?: string;
    heatmapPresignedImageUrl?: string;
}

interface CosmeticGradingImageDto {
    image_type: string;
    presigned_image_url?: string | null;
    detected_damage_types: string[];
    debug_presigned_image_url?: string | null;
    privacy_presigned_image_url?: string | null;
    damage_presigned_image_url?: string | null;
    heatmap_presigned_image_url?: string | null;
}

function toCosmeticGradingImage(dto: CosmeticGradingImageDto): CosmeticGradingImage {
    return {
        imageType: dto.image_type,
        presignedImageUrl: dto.presigned_image_url ?? undefined,
        detectedDamageTypes: dto.detected_damage_types,
        debugPresignedImageUrl: dto.debug_presigned_image_url ?? undefined,
        privacyPresignedImageUrl: dto.privacy_presigned_image_url ?? undefined,
        damagePresignedImageUrl: dto.damage_presigned_image_url ?? undefined,
        heatmapPresignedImageUrl: dto.heatmap_presigned_image_url ?? undefined,
    };
}

export type CosmeticGradingUserOutcome = "FAILED" | "PASSED";

export interface CosmeticGradingUserOverride {
    imageType: string;
    userOutcome: CosmeticGradingUserOutcome;
    userComment?: string;
    presignedImageUrl?: string;
    isCurrentImage: boolean;
}

interface CosmeticGradingUserOverrideDto {
    image_type: string;
    user_outcome: CosmeticGradingUserOutcome;
    user_comment?: string | null;
    presigned_image_url?: string | null;
    is_current_image: boolean;
}

function toCosmeticGradingUserOverride(dto: CosmeticGradingUserOverrideDto): CosmeticGradingUserOverride {
    return {
        imageType: dto.image_type,
        userOutcome: dto.user_outcome,
        userComment: dto.user_comment ?? undefined,
        presignedImageUrl: dto.presigned_image_url ?? undefined,
        isCurrentImage: dto.is_current_image,
    };
}

export interface CosmeticGradingResult {
    images: CosmeticGradingImage[];
    userOverrides: CosmeticGradingUserOverride[];
    detectedDamageTypes: Record<string, string[]>;
    testResults: Record<string, boolean>;
    cosmeticGradingDate?: string;
}

interface CosmeticGradingResultDto {
    images: CosmeticGradingImageDto[];
    user_overrides: CosmeticGradingUserOverrideDto[];
    detected_damage_types: Record<string, string[]>;
    test_results: Record<string, boolean>;
    cosmetic_grading_date?: string;
}

function toCosmeticGradingResult(dto: CosmeticGradingResultDto): CosmeticGradingResult {
    return {
        images: dto.images.map((each) => toCosmeticGradingImage(each)),
        userOverrides: dto.user_overrides.map((each) => toCosmeticGradingUserOverride(each)),
        detectedDamageTypes: dto.detected_damage_types,
        testResults: dto.test_results,
        cosmeticGradingDate: dto.cosmetic_grading_date,
    };
}

class AssetGradingService {
    public fetchGradingStepProgress(
        groupUuid: string,
        abortController?: AbortController
    ): Promise<GradingStepProgress | undefined> {
        return apiGatewayService
            .invokeApi(`${GRADING_STEPS_PATH}/${groupUuid}`, "GET", null, {
                abortSignal: abortController?.signal,
                apiType: ApiType.ARTHUR,
            })
            .then((dto: GradingStepProgressDto) => toGradingStepProgress(dto));
    }

    public isSmartGradingEnabled(groupUuid: string, abortController?: AbortController): Promise<boolean> {
        return this.fetchGradingStepProgress(groupUuid, abortController)
            .then((progress) => progress?.enabled === true)
            .catch((error: Error & { status?: number }) => {
                if (error.status === HTTP_NOT_FOUND) {
                    return false;
                }
                throw error;
            });
    }

    public hasSmartGradingEverStarted(abortController?: AbortController): Promise<boolean> {
        return apiGatewayService
            .invokeApi(GRADING_STEPS_PATH, "GET", null, {
                abortSignal: abortController?.signal,
                apiType: ApiType.ARTHUR,
            })
            .then((dto: GradingStepProgressListDto) => dto.grading_steps.length > 0);
    }

    public saveGradingStep(
        groupUuid: string,
        step: GradingStep,
        enabled: boolean,
        workflowUuid?: string,
        abortController?: AbortController,
        isRerun?: boolean
    ): Promise<void> {
        return apiGatewayService.invokeApi<SaveGradingStepDto, void>(
            `${GRADING_STEPS_PATH}/${groupUuid}`,
            "POST",
            {
                step: step,
                enabled: enabled,
                workflow_uuid: workflowUuid,
                ...(isRerun && { is_rerun: isRerun }),
            },
            {
                abortSignal: abortController?.signal,
                emptyResponse: true,
                apiType: ApiType.ARTHUR,
            }
        );
    }

    public launchGrading(
        workflowUuid: string,
        body: AssetSearchRequest,
        abortController?: AbortController
    ): Promise<GradingLaunchResponse> {
        return apiGatewayService
            .invokeApi<AssetSearchRequestDto, GradingLaunchResponseDto>(
                ASSET_MANAGEMENT_PATH + "assets/grade/" + workflowUuid,
                "POST",
                toAssetSearchRequestDto(body),
                {
                    abortSignal: abortController?.signal,
                    jsonResponse: true,
                    apiType: ApiType.OLIVER,
                }
            )
            .then((dto) => ({ assetsToGradeCount: dto.assets_to_grade_count }));
    }

    public fetchGradedSummary(body?: AssetSearchRequest, abortController?: AbortController): Promise<GradedSummary> {
        return apiGatewayService
            .invokeApi<AssetSearchRequestDto, GradedSummaryDto>(
                ASSET_MANAGEMENT_PATH + "assets/grade/summary",
                "POST",
                toAssetSearchRequestDto(body ?? { paths: [] }),
                {
                    abortSignal: abortController?.signal,
                    jsonResponse: true,
                    apiType: ApiType.OLIVER,
                }
            )
            .then((dto) => ({
                totalGraded: dto.total_graded,
                totalPending: dto.total_pending,
                totalLocked: dto.total_locked,
            }));
    }

    public fetchAssetValues(body: AssetSearchRequest, abortController?: AbortController): Promise<AssetValues> {
        return apiGatewayService
            .invokeApi<AssetSearchRequestDto, AssetValuesDto>(
                ASSET_MANAGEMENT_PATH + "assets/values",
                "POST",
                toAssetSearchRequestDto(body),
                {
                    abortSignal: abortController?.signal,
                    jsonResponse: true,
                    apiType: ApiType.OLIVER,
                }
            )
            .then((dto) => ({ value: dto.value, upliftValue: dto.uplift_value }));
    }

    public fetchResolveRiskSummary(
        body: AssetSearchRequest,
        abortController: AbortController
    ): Promise<ResolveRiskSummary> {
        return apiGatewayService
            .invokeApi(ASSET_MANAGEMENT_PATH + "assets/grade/risk-summary", "POST", toAssetSearchRequestDto(body), {
                abortSignal: abortController.signal,
                jsonResponse: true,
                apiType: ApiType.OLIVER,
            })
            .then((dto: ResolveRiskSummaryDto) => toResolveRiskSummary(dto));
    }

    public fetchWorkflowsForGrading(abortController: AbortController): Promise<WorkflowsForGrading> {
        const dtoPromise = this.fetchWorkflowsForGradingDto(abortController);
        return dtoPromise.then((dto) => {
            const customWorkflows = dto.workflows.filter(
                (each) => dto.templates.find((template) => template.workflow_uuid === each.uuid) === undefined
            );
            return toWorkflowsForGrading({ templates: dto.templates, workflows: customWorkflows });
        });
    }

    private fetchWorkflowsForGradingDto(abortController: AbortController): Promise<WorkflowsForGradingDto> {
        return apiGatewayService.invokeApi(WORKFLOWS_PATH, "GET", null, {
            abortSignal: abortController.signal,
            jsonResponse: true,
            apiType: ApiType.OLIVER,
        });
    }

    public fetchCosmeticGradingResult(
        assetUuid: string,
        abortController?: AbortController
    ): Promise<CosmeticGradingResult> {
        return apiGatewayService
            .invokeApi(ASSET_MANAGEMENT_PATH + "assets/" + assetUuid + "/cosmetic-grading", "GET", null, {
                abortSignal: abortController?.signal,
                jsonResponse: true,
                apiType: ApiType.OLIVER,
            })
            .then((dto: CosmeticGradingResultDto) => toCosmeticGradingResult(dto));
    }
}

export const assetGradingService = new AssetGradingService();
